// Dev-only fake backend for the member-review page (VUE_APP_MEMBER_REVIEW_MOCK=true).
// Shapes mirror the rep-24hrs-edit endpoints.
import { expire } from '@/resource/memberReviewApi';

const SUBMITTED_KEY = 'memberReviewMockSubmitted';
const FORCE_STATUS_KEY = 'memberReviewMockStatus';

const f = (field_name, value, extra = {}) => ({ field_name, value, required: false, ...extra });
const req = { required: true };
const STATES = [{ name: 'Texas', value: 'TX' }, { name: 'California', value: 'CA' }];

// Dependent health questions, in the shape the API returns (dependent pop-ups show these).
const dependentHealthInfo = () => ({
    notes: '',
    condition_questions: [
        { question_id: 11, question_text: 'Has this dependent been diagnosed with or treated for any medical condition in the last 5 years?', selected_answer: 0, conditions: [] },
        { question_id: 12, question_text: 'Is this dependent currently taking any prescription medication?', selected_answer: 0, conditions: [] },
    ],
    medication_question: { question_id: 13, question_text: 'Does this dependent take any medication or Rx injections?', selected_answer: 0, medications: [] },
});

const FIXTURES = {
    tabs: [
        { tab_name: 'employer_info' },
        { tab_name: 'beneficiary_info' },
        { tab_name: 'primary_health_info' },
        { tab_name: 'dependent_info' },
    ],
    'primary-info': [
        f('first_name', 'Jane', req),
        f('middle_name', ''),
        f('last_name', 'Doe', req),
        f('gender', 'F', { ...req, options: [{ name: 'Male', value: 'M' }, { name: 'Female', value: 'F' }] }),
        f('birthdate', '04/12/1985', req),
        f('marital_status', 'M', { options: [{ name: 'Single', value: 'S' }, { name: 'Married', value: 'M' }] }),
        f('ssn', '123456789', req),
        f('height_feet', 5),
        f('height_inch', 6),
        f('weight', 140),
        f('prior_coverage_flag', 0),
    ],
    'contact-info': [
        f('email_address', 'jane.doe@example.com', req),
        f('phone_number', '5551234567', req),
        f('address1', '100 Main St', req),
        f('address2', ''),
        f('city', 'Austin', req),
        f('state', 'TX', { ...req, options: STATES }),
        f('zip', '73301', req),
        f('with_validation', 1),
    ],
    'employer-info': [
        f('name', 'Acme Corp', req),
        f('phone', '5559876543'),
        f('address1', '1 Acme Way'),
        f('address2', ''),
        f('city', 'Austin'),
        f('state', 'TX', { options: STATES }),
        f('zip', '73301'),
        f('occupation', 'Engineer'),
        f('start_date', '2020-01-15'),
        f('status', 'A', { options: [{ name: 'Active', value: 'A' }, { name: 'Inactive', value: 'I' }] }),
        f('compensation_type', 'salary', { options: [{ name: 'Salary', value: 'salary' }, { name: 'Hourly', value: 'hourly' }] }),
        f('average_work_week', 40),
        f('emp_industry', 'Software'),
        f('emp_num_employed', 50),
        f('emp_barg_unit', 0),
        f('emp_vac_policy', 0),
        f('with_validation', 1),
    ],
    'billing-info': [
        f('payment_type', 'eft', { ...req, options: [{ name: 'Bank (EFT)', value: 'eft' }, { name: 'Card', value: 'cc' }] }),
        f('account_name', 'Jane Doe', req),
        f('account_holder_type', 'individual', { options: [{ name: 'Individual', value: 'individual' }, { name: 'Company', value: 'company' }] }),
        f('account_type', 'checking', { options: [{ name: 'Checking', value: 'checking' }, { name: 'Savings', value: 'savings' }] }),
        f('routing_number', '111000025', req),
        f('account_number', '123456789', req),
        f('bank_name', 'Bank of Example'),
    ],
    'beneficiary-info': {
        1: [
            f('first_name', 'John', req),
            f('middle_name', ''),
            f('last_name', 'Doe', req),
            f('relation', 'spouse', { ...req, options: [{ name: 'Spouse', value: 'spouse' }, { name: 'Child', value: 'child' }] }),
            f('birthdate', '06/01/1984'),
            f('ssn', '987654321'),
            f('beneficiary_percentage', 100, req),
            f('is_contigent', 0),
        ],
    },
    'primary-health-info': {
        notes: '',
        condition_questions: [
            { question_id: 1, question_text: 'Do you currently have any medical condition?', selected_answer: 0, conditions: [] },
        ],
        medication_question: null,
    },
    'dependent-info': {
        is_iha: 0,
        tier: '',
        dependents: [
            {
                primary_info: [
                    f('first_name', 'John', req),
                    f('middle_name', ''),
                    f('last_name', 'Doe', req),
                    f('relationship', 's', req),
                    f('gender', 'M', { ...req, options: [{ name: 'Male', value: 'M' }, { name: 'Female', value: 'F' }] }),
                    f('birthdate', '06/01/1984', req),
                    f('ssn', '987654321'),
                    f('height_feet', 5),
                    f('height_inch', 11),
                    f('weight', 180),
                ],
                health_info: dependentHealthInfo(),
            },
            {
                primary_info: [
                    f('first_name', 'Jimmy', req),
                    f('middle_name', ''),
                    f('last_name', 'Doe', req),
                    f('relationship', 'c', req),
                    f('gender', 'M', { ...req, options: [{ name: 'Male', value: 'M' }, { name: 'Female', value: 'F' }] }),
                    f('birthdate', '03/20/2015', req),
                    f('ssn', '555667777'),
                    f('height_feet', 4),
                    f('height_inch', 2),
                    f('weight', 60),
                ],
                health_info: dependentHealthInfo(),
            },
        ],
    },
    'policy-tiers': ['IF'],
};

function readStorage(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
}
function writeStorage(key, value) {
    try {
        if (value === null) window.sessionStorage.removeItem(key);
        else window.sessionStorage.setItem(key, value);
    } catch (e) { /* storage unavailable */ }
}

const ok = (data, message = 'Success') =>
    new Promise((resolve) => setTimeout(() => resolve({ data: { status: 'success', message, data } }), 300));

const fail = (status, message) => {
    const error = { message, response: { status, data: { status: 'error', message } } };
    if (status === 401 || status === 403) expire();
    return new Promise((resolve, reject) => setTimeout(() => reject(error), 300));
};

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

export default {
    handle(method, path, data) {
        const forced = readStorage(FORCE_STATUS_KEY);
        if (forced) {
            writeStorage(FORCE_STATUS_KEY, null);
            return fail(Number(forced), `Mock forced ${forced}`);
        }

        if (method === 'get' && path === 'status') return ok({ submitted: readStorage(SUBMITTED_KEY) === '1' });
        // OTP: the mock accepts any 6 digits except 000000 (to try the error path).
        if (method === 'get' && path === 'otp') return ok({ sent_to: ['j***@example.com'], verified: false });
        if (method === 'post' && path === 'otp/send') return ok({ sent_to: ['j***@example.com'] }, 'Code sent to j***@example.com.');
        if (method === 'post' && path === 'otp/verify') {
            if (!data || data.otp === '000000') return fail(422, 'Incorrect code. You have 2 attempts left.');
            return ok({ access_token: 'mock-access-token', expires_in: 1800 }, 'Verified.');
        }
        if (method === 'get' && FIXTURES[path] !== undefined) return ok(clone(FIXTURES[path]));
        if (method === 'patch') return ok(null, 'Updated successfully.');
        if (method === 'post' && path === 'validate-single-dependent') return ok(null, 'Dependent is valid.');
        if (method === 'post' && path === 'pregnancy-restriction') return ok(null, 'Pregnancy restriction recorded');
        if (method === 'post' && path === 'validate-routing-number') return ok({ result: { bank_name: 'Bank of Example' } });
        if (method === 'post' && path === 'submit') {
            if (readStorage(SUBMITTED_KEY) === '1') return fail(409, 'This review has already been submitted.');
            writeStorage(SUBMITTED_KEY, '1');
            return ok(null, 'Thank you. Your review has been submitted.');
        }
        return fail(404, `Mock has no handler for ${method.toUpperCase()} ${path}`);
    },
};
